import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, RefreshIcon, SendIcon } from 'assets/icons';
import UseContext from 'hooks/useContext';
import Button from 'components/button';
import CopyToClipboard from 'components/copyToClipboard';
import UseModal from 'components/modal/useModal';
import ApiDatabase from 'server';

const normalizeEmail = (email) => (email ?? '').trim().toLowerCase();

const contactName = (contact) =>
  [contact.firstname, (contact.lastname ?? '').toUpperCase()]
    .filter(Boolean)
    .join(' ') || contact.email;

const SlideOverCompanyHubspotContacts = ({ companyInfo }) => {
  const { toggle } = UseModal();
  const { language, setModalData } = UseContext();
  const t = (key) => language.getValue(`companyContacts.hubspot.${key}`, '');
  const idCompany = companyInfo?._id;
  const [contacts, setContacts] = useState([]);
  const [status, setStatus] = useState('loading');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Requests for two companies do not cancel each other (their urls differ):
  // only the latest one may fill the block.
  const latestRequest = useRef(null);

  const load = useCallback(() => {
    if (!idCompany) return;
    const request = {};
    latestRequest.current = request;
    ApiDatabase.getCompanyHubspotContacts(
      { idCompany },
      (data) => {
        if (latestRequest.current !== request) return;
        setContacts(Array.isArray(data?.data) ? data.data : []);
        setStatus('ready');
        setIsRefreshing(false);
      },
      (error) => {
        if (
          latestRequest.current !== request ||
          error?.name === 'CanceledError'
        )
          return;
        setStatus('error');
        setIsRefreshing(false);
      },
    );
  }, [idCompany]);

  useEffect(() => {
    setContacts([]);
    setStatus('loading');
    setIsRefreshing(false);
    setIsCollapsed(false);
    load();
  }, [load]);

  // An invitation sent from this block adds the member to the company: the
  // contact leaves the list at once, without reading HubSpot again.
  const memberEmails = new Set(
    (companyInfo?.employees ?? [])
      .map((entry) => normalizeEmail(entry.user?.email))
      .filter(Boolean),
  );
  const visible = contacts.filter(
    (contact) => !memberEmails.has(normalizeEmail(contact.email)),
  );

  if (status === 'ready' && visible.length === 0) return null;

  const refresh = () => {
    setIsRefreshing(true);
    load();
  };
  const retry = () => {
    setStatus('loading');
    load();
  };
  const invite = (contact) => {
    setModalData({
      idCompany,
      origin: 'hubspot',
      hubspotContactId: contact.hubspotId,
      prefill: {
        email: contact.email,
        firstName: contact.firstname ?? '',
        lastName: contact.lastname ?? '',
        phone: contact.phone ?? '',
      },
    });
    toggle('add_company_member');
  };

  return (
    <div className={'flex flex-col pt-2'}>
      <div className={'flex gap-2 justify-between items-start pb-2'}>
        <div className={'min-w-0'}>
          <div className={'flex gap-2 items-center'}>
            <div className={'font-semibold'}>{t('title')}</div>
            {status === 'ready' ? (
              <div
                data-testid={'hubspot-contacts-count'}
                className={
                  'my-auto bg-yellow-100 text-yellow-800 rounded px-1 flex items-center justify-center min-w-[20px]'
                }
              >
                {visible.length}
              </div>
            ) : null}
          </div>
          <div className={'text-gray-500'}>
            {/* A function replacer: a plain string would read `$&` or `$$` in
                the company name as replacement patterns. */}
            {t('description').replace(
              '{company}',
              () => companyInfo?.name_commercial ?? '',
            )}
          </div>
        </div>
        <div className={'flex gap-2 shrink-0'}>
          <Button
            size={'ICON'}
            color={'SECONDARY'}
            disabled={isRefreshing || status === 'loading'}
            onClick={refresh}
          >
            <span className={isRefreshing ? 'animate-spin flex' : 'flex'}>
              <RefreshIcon wh={20} color={'#374151'} />
            </span>
            <span className={'sr-only'}>{t('refresh')}</span>
          </Button>
          <Button
            size={'ICON'}
            color={'SECONDARY'}
            aria-expanded={!isCollapsed}
            onClick={() => setIsCollapsed((collapsed) => !collapsed)}
          >
            {isCollapsed ? (
              <ChevronRight wh={20} color={'#374151'} />
            ) : (
              <ChevronDown wh={20} color={'#374151'} />
            )}
            <span className={'sr-only'}>
              {t(isCollapsed ? 'expand' : 'collapse')}
            </span>
          </Button>
        </div>
      </div>
      {isCollapsed ? null : status === 'loading' ? (
        <div
          className={'flex flex-col gap-3 py-2 animate-pulse'}
          data-testid={'hubspot-contacts-loading'}
        >
          {[0, 1].map((row) => (
            <div key={row} className={'flex flex-col gap-2'}>
              <div className={'h-4 w-1/3 bg-gray-200 rounded'} />
              <div className={'h-4 w-1/2 bg-gray-200 rounded'} />
            </div>
          ))}
        </div>
      ) : status === 'error' ? (
        <div className={'py-2 flex gap-2 items-center justify-between'}>
          <div className={'text-red-500'}>{t('error')}</div>
          <Button size={'SM'} color={'SECONDARY'} onClick={retry}>
            {t('retry')}
          </Button>
        </div>
      ) : (
        visible.map((contact) => (
          <div
            key={contact.hubspotId}
            className={'py-2 border-b flex justify-between gap-2 items-center'}
          >
            <div className={'min-w-0'}>
              <div className={'truncate'}>{contactName(contact)}</div>
              <div className={'text-gray-500 flex [&>*]:min-w-0'}>
                <CopyToClipboard text={contact.email}>
                  <div className={'truncate'} title={contact.email}>
                    {contact.email}
                  </div>
                </CopyToClipboard>
              </div>
            </div>
            <Button
              size={'SM'}
              color={'SECONDARY'}
              onClick={() => invite(contact)}
            >
              <div className={'flex gap-1 items-center'}>
                <SendIcon wh={20} color={'#374151'} />
                <span>{t('invite')}</span>
              </div>
            </Button>
          </div>
        ))
      )}
    </div>
  );
};

export default SlideOverCompanyHubspotContacts;
